import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon "Beyond The Frame" video yarışması medya planı.
 * 30 Haziran – 16 Ağustos 2026 (48 gün), FB + IG, hedef: Trafik.
 * Bütçe modeli Canon Nisan 2026 planıyla aynı: AHK %10, Stopaj %15, Tasarım ayrı.
 */
export default function CanonBeyondFrame2026() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe kırılımı ───────────────────────────────────────────────────────
  const TOTAL = 340000;
  const AHK_RATE = 0.10;
  const AHK_FEE = Math.round(TOTAL * AHK_RATE);   // 34.000 — Ajans Hizmet Komisyonu
  const DESIGN = 35000;                            // Tasarım
  const MEDIA_BRUT = TOTAL - AHK_FEE - DESIGN;     // 271.000 (stopaj dahil medya)
  const STOPAJ_RATE = 0.15;
  const STOPAJ = Math.round(MEDIA_BRUT * STOPAJ_RATE); // 40.650
  const NET = MEDIA_BRUT - STOPAJ;                 // 230.350 — Meta'da gerçek harcanan

  // ── Süre ──────────────────────────────────────────────────────────────────
  const DAYS = 48; // 30 Haziran → 16 Ağustos dahil
  const dailyNet = NET / DAYS;

  // ── İçerik bazında bütçe + format dağılımı ────────────────────────────────
  // Paid medya 30 Haz–16 Ağu. En büyük pay: Duyuru/Lansman, Yarışmayı Anlatan
  // Carousel ve "Katılmak için Son 20 Gün"; kalanlara serpiştirilir.
  // 1 Ekim kazanan duyurusu paid flight dışı (organik) → bütçesiz.
  // İçerik bütçesi TEK KAYNAK; format tablosu bundan türetilir (Partner içerik yok).
  const FREQ = 3.3; // 48 günlük kampanya için tahmini ortalama frekans
  const FMT_META = {
    'Own Post':  { size: '1080×1350', placement: 'IG/FB Feed',          cpm: 30, cpc: 2.80 },
    'Own Story': { size: '1080×1920', placement: 'IG/FB Stories',       cpm: 32, cpc: 3.20 },
    'Carousel':  { size: '1080×1080', placement: 'IG/FB Feed Carousel', cpm: 28, cpc: 2.60 },
  };
  const contentDefs = [
    { title: 'Duyuru / Lansman',            format: 'Own Post',  tone: 'post',  start: '2026-06-30', end: '2026-08-16', share: 0.24 },
    { title: 'Lansman Story',               format: 'Own Story', tone: 'story', start: '2026-06-30', end: '2026-07-09', share: 0.08 },
    { title: '“Yarışmaya Katıl” Story',     format: 'Own Story', tone: 'story', start: '2026-07-03', end: '2026-08-16', share: 0.14 },
    { title: 'Yarışmayı Anlatan Carousel',  format: 'Carousel',  tone: 'caro',  start: '2026-07-14', end: '2026-08-16', share: 0.22 },
    { title: '“Katılmak için Son 20 Gün”',  format: 'Own Post',  tone: 'post',  start: '2026-07-28', end: '2026-08-16', share: 0.20 },
    { title: '“4 Adımda Nasıl Katılırsın”', format: 'Own Post',  tone: 'post',  start: '2026-08-06', end: '2026-08-16', share: 0.12 },
    { title: '“Kazananları Açıklıyoruz”',   format: 'Own Post',  tone: 'win',   start: '2026-10-01', end: '2026-10-05', organic: true },
  ];
  const paidDefs = contentDefs.filter((c) => !c.organic);
  let allocC = 0;
  const content = contentDefs.map((c) => {
    let budget = 0;
    if (!c.organic) {
      const isLastPaid = c === paidDefs[paidDefs.length - 1];
      budget = isLastPaid ? NET - allocC : Math.round(NET * c.share);
      allocC += budget;
    }
    return { ...c, budget };
  });
  // Format tablosu = içerik bütçelerinin formata göre toplamı
  const formats = Object.keys(FMT_META).map((name) => {
    const meta = FMT_META[name];
    const budget = content.filter((c) => c.format === name).reduce((s, c) => s + c.budget, 0);
    const impressions = Math.round((budget / meta.cpm) * 1000);
    const clicks = Math.round(budget / meta.cpc);
    const reach = Math.round(impressions / FREQ);
    return { name, ...meta, budget, impressions, clicks, reach };
  });

  const totals = formats.reduce((t, f) => ({
    budget: t.budget + f.budget,
    impressions: t.impressions + f.impressions,
    clicks: t.clicks + f.clicks,
  }), { budget: 0, impressions: 0, clicks: 0 });
  const blendedCpc = totals.budget / totals.clicks;
  const blendedCpm = (totals.budget / totals.impressions) * 1000;
  const estReach = Math.round(totals.impressions / FREQ);
  const ctr = (totals.clicks / totals.impressions) * 100;

  // ── İçerik takvimi (timeline) ──────────────────────────────────────────────
  // 7 içerik: yayına giriş tarihi + yayında kalış süresi. Paid medya 30 Haz–16 Ağu;
  // 1 Ekim kazanan duyurusu yarışma kapanışı sonrası ayrı bir burst.
  const TL_START = new Date('2026-06-30');
  const TL_END = new Date('2026-10-12');
  const TL_SPAN = (TL_END - TL_START) / 86400000;
  const tlOff = (d) => (new Date(d) - TL_START) / 86400000;
  const tlPct = (d) => (tlOff(d) / TL_SPAN) * 100;
  const fmtDay = (d) => new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  const durDays = (s, e) => Math.round(tlOff(e) - tlOff(s));
  const TONES = { post: '#CC0000', story: '#6366f1', caro: '#f59e0b', win: '#10b981' };
  const evalPhase = { start: '2026-08-16', end: '2026-10-01' };
  const tlMonths = [
    { label: 'Temmuz',  date: '2026-07-01' },
    { label: 'Ağustos', date: '2026-08-01' },
    { label: 'Eylül',   date: '2026-09-01' },
    { label: 'Ekim',    date: '2026-10-01' },
  ];

  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(slideRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      // tek görseli birden çok sayfaya böl
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save('Canon_Beyond_The_Frame_Medya_Plani.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">Beyond The Frame — Video Yarışması Medya Planı</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Trafik · 30 Haziran – 16 Ağustos 2026</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya detayı */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon'un <strong>“Beyond The Frame”</strong> video yarışmasının duyurusu. 30 Haziran – 16 Ağustos 2026
              tarihleri arasında (48 gün) yayında. Amaç: amatör video içerik üreticilerini Canon ailesine katmak,
              yarışmaya katılım (entry) için brief'e/link in bio'ya trafik çekmek. Yönlendirme reklam hedefi:
              <strong> Trafik</strong>.
            </p>
          </div>

          {/* Hedefleme */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Hedefleme</p>

            {/* Demografi + kanal kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Hedef Kitle" value="Videographer" sub="amatör video içerik üreticileri" tone="red" />
              <Stat label="Yaş / Cinsiyet" value="18–45" sub="Tüm cinsiyetler · 18–34 ağırlıklı" />
              <Stat label="Dil" value="Türkçe" sub="TR yerleşik" />
              <Stat label="Platform" value="FB + IG" sub="Meta Ads · otomatik yerleşim" />
            </div>

            {/* Lokasyon */}
            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Lokasyon</span>
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                <strong>Tüm Türkiye</strong> — içerik üreticisi yoğunluğu yüksek büyükşehir ağırlıklı
                (İstanbul, Ankara, İzmir, Antalya, Bursa, Eskişehir). Üniversite şehirleri (genç içerik üretici havuzu)
                ek ağırlıkla hedeflenir.
              </p>
            </div>

            {/* İlgi alanları */}
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">İlgi Alanları (Detaylı Hedefleme)</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['Videografi', 'Video düzenleme', 'Film yapımı & sinematografi', 'İçerik üreticiliği', 'Vlog & YouTube',
                  'Kısa film & belgesel', 'Fotoğrafçılık', 'Aynasız fotoğraf makineleri', 'Drone & gimbal', 'Adobe Premiere / DaVinci / Final Cut',
                  'Reels & TikTok içerik üretimi', 'Sinema & yönetmenlik', 'Görsel sanatlar & tasarım', 'Seyahat & vlog'].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">{t}</span>
                ))}
              </div>
            </div>

            {/* Davranışlar */}
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Davranışlar & Demografi Detayı</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['Üniversite öğrencileri / yeni mezunlar', 'Sayfa yöneticileri (içerik üreticiler)', 'Teknoloji erken benimseyenler', 'Etkin IG/FB içerik paylaşanlar'].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{t}</span>
                ))}
              </div>
            </div>

            {/* Özel & Benzer kitleler */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                <span className="text-[10px] font-medium uppercase text-emerald-700">Özel Kitleler (Custom Audience)</span>
                <ul className="text-[11px] text-gray-700 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
                  <li>Son 365 gün IG/FB sayfa & post etkileşimcileri</li>
                  <li>Video izleyiciler (%25+ izleme)</li>
                  <li>Yarışma landing / brief sayfası ziyaretçileri (Pixel) — retargeting</li>
                </ul>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                <span className="text-[10px] font-medium uppercase text-emerald-700">Benzer Kitleler (Lookalike)</span>
                <ul className="text-[11px] text-gray-700 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
                  <li>Etkileşimciler LAL %1–3 (TR)</li>
                  <li>Video izleyiciler LAL %2 (TR)</li>
                  <li>Landing ziyaretçileri LAL %1–5 (TR)</li>
                </ul>
              </div>
            </div>

            <p className="text-[10px] text-gray-400 mt-2">
              Trafik hedefinde geniş ilgi alanı + LAL ile katılımcı havuzunu büyüt; son haftalarda landing ziyaretçileri
              ve video izleyicileri retargeting ile “deadline / son günler” mesajına yönlendir. İlgi alanları ve kitle
              eşikleri performansa göre kampanya boyunca optimize edilir.
            </p>
          </div>

          {/* Bütçe kırılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Toplam Bütçe" value={fmtCur(TOTAL)} sub="müşteri toplam" />
              <Stat label="AHK (Ajans %10)" value={'-' + fmtCur(AHK_FEE)} sub={`${fmtCur(TOTAL)} × 0.10`} tone="indigo" />
              <Stat label="Tasarım" value={'-' + fmtCur(DESIGN)} sub="içerik üretimi" tone="indigo" />
              <Stat label="Stopaj (%15)" value={'-' + fmtCur(STOPAJ)} sub={`${fmtCur(MEDIA_BRUT)} × 0.15`} tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Günlük ortalama net harcama ≈ {fmtCur(dailyNet)} ({DAYS} gün). AHK + Stopaj + Tasarım toplam 340K'ya dahildir.
            </p>
          </div>

          {/* İçerik / format dağılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Format Bazında Bütçe Dağılımı (3 Format)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">İçerik</th>
                    <th className="text-left px-3 py-2 font-semibold">Boyut</th>
                    <th className="text-left px-3 py-2 font-semibold">Yerleşim</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe (NET)</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim*</th>
                    <th className="text-right px-3 py-2 font-semibold">Tıklama</th>
                    <th className="text-right px-3 py-2 font-semibold">CPC</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {formats.map((f) => (
                    <tr key={f.name} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-800">{f.name}</td>
                      <td className="px-3 py-2 text-gray-500">{f.size}</td>
                      <td className="px-3 py-2 text-gray-500">{f.placement}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtCur(f.budget)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(f.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(f.reach)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(f.clicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{f.cpc.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{f.cpm.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(estReach)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.clicks)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpc.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpm.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              *Erişim, ~{FREQ} ortalama frekans varsayımıyla tahminidir. Tahmini CTR ≈ %{ctr.toFixed(2)}.
              Sayılar Meta açık artırma dinamiklerine göre değişebilir; optimizasyonla yön verilir.
            </p>
          </div>

          {/* Projeksiyon özeti */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam Tıklama" value={fmtInt(totals.clicks)} sub="link tıklaması (tahmini)" tone="green" />
            <Stat label="Ort. CPC" value={'₺' + blendedCpc.toFixed(2)} />
            <Stat label="Toplam Gösterim" value={fmtInt(totals.impressions)} />
            <Stat label="Tahmini Erişim" value={fmtInt(estReach)} sub={`~${FREQ} frekans`} />
          </div>

          {/* İçerik Takvimi (Timeline) */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-1">İçerik Takvimi (7 İçerik)</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Her içeriğin bütçe payı (NET üzerinden), yayına giriş tarihi ve yayında kalacağı süre. En büyük pay
              Duyuru/Lansman, Yarışmayı Anlatan Carousel ve "Son 20 Gün" içeriklerinde; kalanlara serpiştirilir.
              Paid medya 30 Haziran – 16 Ağustos; 1 Ekim kazanan duyurusu yarışma kapanışından sonra organik burst.
            </p>
            <div className="rounded-xl border border-gray-200 p-4 overflow-hidden">
              {/* Ay başlıkları */}
              <div className="flex">
                <div className="w-40 md:w-52 shrink-0" />
                <div className="relative flex-1 h-4">
                  {tlMonths.map((m) => (
                    <span key={m.label} className="absolute top-0 text-[9px] font-semibold text-gray-400 -translate-x-1/2" style={{ left: `${tlPct(m.date)}%` }}>{m.label}</span>
                  ))}
                </div>
              </div>

              {/* Gövde: ay çizgileri + satırlar */}
              <div className="relative mt-1.5">
                {/* dikey ay çizgileri */}
                <div className="absolute inset-0 flex pointer-events-none">
                  <div className="w-40 md:w-52 shrink-0" />
                  <div className="relative flex-1">
                    {tlMonths.map((m) => (
                      <div key={m.label} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${tlPct(m.date)}%` }} />
                    ))}
                  </div>
                </div>

                {/* Değerlendirme fazı (yayın yok) */}
                <div className="flex items-center h-7 relative">
                  <div className="w-40 md:w-52 shrink-0 pr-3 text-right">
                    <span className="text-[9px] text-gray-400 italic">Değerlendirme & Jüri</span>
                  </div>
                  <div className="relative flex-1 h-7">
                    <div
                      className="absolute top-1/2 -translate-y-1/2 h-3 rounded-full border border-dashed border-gray-300"
                      style={{
                        left: `${tlPct(evalPhase.start)}%`,
                        width: `${tlPct(evalPhase.end) - tlPct(evalPhase.start)}%`,
                        background: 'repeating-linear-gradient(45deg,#f8fafc,#f8fafc 4px,#eef2f7 4px,#eef2f7 8px)',
                      }}
                    />
                  </div>
                </div>

                {/* İçerik satırları */}
                {content.map((it, i) => {
                  const left = tlPct(it.start);
                  const width = Math.max(tlPct(it.end) - left, 1.5);
                  const nearEnd = left > 66;
                  const pctNum = it.organic ? 0 : Math.round((it.budget / NET) * 100);
                  return (
                    <div key={i} className="flex items-center h-12 relative">
                      <div className="w-40 md:w-52 shrink-0 pr-3">
                        <p className="text-[11px] font-semibold text-gray-800 whitespace-nowrap" style={{ lineHeight: '1.5' }}>{it.title}</p>
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[9px] text-gray-400">{it.format}</span>
                          {it.organic
                            ? <span className="text-[8.5px] italic text-gray-400">organik</span>
                            : <span className="text-[9px] font-bold tabular-nums" style={{ color: TONES[it.tone] }}>{fmtCur(it.budget)} · %{pctNum}</span>}
                        </div>
                      </div>
                      <div className="relative flex-1 h-12">
                        <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${left}%`, width: `${width}%` }}>
                          <div className={`absolute bottom-full mb-1 whitespace-nowrap text-[8.5px] font-medium text-gray-500 ${nearEnd ? 'right-0' : 'left-0'}`}>
                            {fmtDay(it.start)} → {fmtDay(it.end)} · {durDays(it.start, it.end)}g
                          </div>
                          <div className="h-4 rounded-full shadow-sm" style={{ background: TONES[it.tone], opacity: it.organic ? 0.45 : 1 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Açıklama (legend) */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-3">
                  {[['#CC0000', 'Own Post'], ['#6366f1', 'Own Story'], ['#f59e0b', 'Carousel'], ['#10b981', 'Kazanan Duyurusu']].map(([c, l]) => (
                    <span key={l} className="flex items-center gap-1.5 text-[9px] text-gray-500">
                      <span className="w-3 h-3 rounded-full" style={{ background: c }} />{l}
                    </span>
                  ))}
                </div>
                <span className="text-[9px] text-gray-400">
                  Toplam paid: <strong className="text-gray-600">{fmtCur(NET)}</strong> · Kazanan duyurusu organik (plan dışı)
                </span>
              </div>
            </div>
          </div>

          {/* Zamanlama */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-bold text-gray-800">Zamanlama</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Yayın: <strong>30 Haziran – 16 Ağustos 2026</strong> (48 gün), yarışma süresi boyunca kesintisiz.
              Duyuru içerikleri başta yoğun, son haftalarda “XX days left / deadline” mesajlarıyla aciliyet (urgency)
              vurgusu. Bütçe gün bazında dengeli dağıtılır; performansa göre format/yerleşim ağırlıkları optimize edilir.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              {new Date().toLocaleDateString('tr-TR')} · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
